import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, User, LogOut, Zap, PenTool } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { apiErr } from "../../lib/api";
import { useT, LanguageSwitcher } from "../../lib/i18n";

const NAV = [
  { key: "home", to: "/" },
  { key: "articles", to: "/articles" },
  { key: "shorts", to: "/shorts" },
  { key: "inkwire", to: "/inkwire" },
  { key: "play", to: "/pti-play" },
  { key: "advertise", to: "/advertise" },
  { key: "creators", to: "/creators" },
  { key: "about", to: "/about" },
];

export function TopNav() {
  const [open, setOpen] = useState(false);
  const { user, logout, becomeCreator } = useAuth();
  const { t } = useT();
  const loc = useLocation();
  const nav = useNavigate();

  const dashLink = user?.role === "admin" ? "/admin" : user?.role === "advertiser" ? "/advertiser" : "/dashboard";
  const isReader = user?.role === "reader";

  const upgrade = async () => {
    try { await becomeCreator(); toast.success("You're a Creator now! Welcome to the Studio."); setOpen(false); nav("/dashboard"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200">
      <div className="pti-container flex items-center justify-between h-24">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src="/logo.png" alt="PictureThisInk — Digital Media Publishing Platform" className="h-20 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={`text-sm font-semibold transition-colors hover:text-pti-red ${loc.pathname === n.to ? "text-pti-red" : "text-pti-black"}`}
            >
              {t(n.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <>
              {isReader ? (
                <button onClick={upgrade} data-testid="nav-become-creator" className="flex items-center gap-1.5 bg-pti-red text-white px-3 py-2 text-sm font-bold hover:bg-pti-black transition-colors">
                  <PenTool size={14} /> {t("becomeCreator")}
                </button>
              ) : (
                <Link to={dashLink} data-testid="nav-dashboard" className="flex items-center gap-2 text-sm font-semibold hover:text-pti-red">
                  <User size={16} /> {user.display_name?.split(" ")[0]}
                </Link>
              )}
              {isReader && <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-600"><User size={16} /> {user.display_name?.split(" ")[0]}</span>}
              <Link to="/my-publishing" data-testid="nav-my-publishing" className="text-sm font-semibold hover:text-pti-red">Publishing</Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="text-gray-500 hover:text-pti-red" aria-label={t("logout")}>
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold hover:text-pti-red">{t("signin")}</Link>
              <Link to="/register" data-testid="nav-join" className="bg-pti-red text-white px-4 py-2 text-sm font-bold hover:bg-pti-redhover transition-colors flex items-center gap-1">
                <Zap size={14} /> {t("join")}
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSwitcher />
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="pti-container py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-base font-semibold py-1">{t(n.key)}</Link>
            ))}
            <div className="border-t border-gray-200 pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  {isReader ? (
                    <button onClick={upgrade} data-testid="mobile-become-creator" className="flex items-center gap-1.5 bg-pti-red text-white px-4 py-2 font-bold justify-center"><PenTool size={15} /> {t("becomeCreator")}</button>
                  ) : (
                    <Link to={dashLink} onClick={() => setOpen(false)} className="font-semibold">{t("dashboard")}</Link>
                  )}
                  <Link to="/my-publishing" onClick={() => setOpen(false)} data-testid="mobile-my-publishing" className="font-semibold">My Publishing</Link>
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left font-semibold text-pti-red">{t("logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="font-semibold">{t("signin")}</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="bg-pti-red text-white px-4 py-2 font-bold text-center">{t("join")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
